import { useEffect, useState, useCallback } from "react"
import {useSelector, useDispatch} from 'react-redux'
import Calendar from "../../components/calendar/Calendar"
import ReservationSelector from "../../components/reservationSelector/ReservationSelector"
import { fetchAteliers, fetchDisponibiliteJour, fetchDisponibiliteMois, sendReservation, clearDisponibiliteMois } from "../../redux/reservationSlice"
import FormReservation from '../../components/formReservation/FormReservation'

const formatDateForApi = (value) => {
  if (!value) return null;
  const dateObj = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const normalizeTimeValue = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) {
    const hour = String(Math.max(0, Math.min(23, Math.floor(value)))).padStart(2, "0");
    return `${hour}:00:00`;
  }
  if (typeof value === "string") {
    if (/^\d{2}:\d{2}:\d{2}$/.test(value)) return value;
    if (/^\d{2}:\d{2}$/.test(value)) return `${value}:00`;
    if (/^\d{1,2}$/.test(value)) {
      return `${String(value).padStart(2, "0")}:00:00`;
    }
  }
  return null;
};

const addHoursToTime = (timeStr, hoursToAdd = 0) => {
  if (!timeStr) return null;
  const base = new Date(`1970-01-01T${timeStr}`);
  if (Number.isNaN(base.getTime())) return null;
  base.setHours(base.getHours() + Number(hoursToAdd || 0));
  return base.toTimeString().slice(0, 8);
};

const Reservation = () => {

  const [dateSelectionnee, setDateSelectionnee] = useState(new Date());
  const [atelierSelectionne, setAtelierSelectionne] = useState("");
  const [creneauSelectionne, setCreneauSelectionne] = useState("");
  const [formData, setFormData] = useState({});

  const dispatch = useDispatch();
  const ateliers = useSelector((state) => state.reservation.ateliers);
  const disponibiliteMois = useSelector((state) => state.reservation.creneaux);
  const disponibiliteJour = useSelector((state) => state.reservation.creneauxJour);

  const handleMonthChange = useCallback(
    ({ year, month }) => {
      if (!year || !month) return;
      const atelier = atelierSelectionne || "";
      dispatch(clearDisponibiliteMois());
      dispatch(
        fetchDisponibiliteMois({
          annee: year,
          mois: month,
          atelier,
        })
      );
    },
    [dispatch, atelierSelectionne]
  );

  const handleSelectionChange = useCallback((selection) => {
    setFormData(selection);
    if (Object.prototype.hasOwnProperty.call(selection, "atelierId")) {
      setAtelierSelectionne(selection.atelierId || "");
    }
    if (Object.prototype.hasOwnProperty.call(selection, "creneauId")) {
      setCreneauSelectionne(selection.creneauId || "");
    }
  }, []);

  const handleReservationSubmit = useCallback(async ({ client_data, commentaire }) => {
    if (!atelierSelectionne) {
      return { success: false, message: "Veuillez sélectionner un atelier avant de réserver." };
    }
    if (!formData?.creneau) {
      return { success: false, message: "Veuillez sélectionner un créneau disponible." };
    }

    const date_atelier = formatDateForApi(formData.date || dateSelectionnee);
    if (!date_atelier) {
      return { success: false, message: "La date sélectionnée est invalide." };
    }

    const heureDebutSource =
      formData.creneau?.creneau_heure_debut ??
      formData.creneau?.heure_debut ??
      formData.creneau?.heure;
    const creneau_heure_debut = normalizeTimeValue(heureDebutSource);
    if (!creneau_heure_debut) {
      return { success: false, message: "Impossible de déterminer l'heure de début du créneau." };
    }

    const heureFinSource =
      formData.creneau?.creneau_heure_fin ??
      formData.creneau?.heure_fin ??
      formData.creneau?.fin;
    let creneau_heure_fin = normalizeTimeValue(heureFinSource);
    if (!creneau_heure_fin) {
      const atelierInfos = ateliers.find(
        (a) => String(a.atelier_id ?? a.id) === String(atelierSelectionne)
      );
      const dureeAtelier = atelierInfos?.duree ?? 2;
      creneau_heure_fin = addHoursToTime(creneau_heure_debut, dureeAtelier);
    }

    if (!creneau_heure_fin) {
      return { success: false, message: "Impossible de déterminer l'heure de fin du créneau." };
    }

    const payload = {
      date_atelier,
      creneau_heure_debut,
      creneau_heure_fin,
      nombre_inscrit: Number(formData?.participants) || 1,
      status: "RESERVE",
      admin_input: false,
      commentaire: commentaire || "",
      atelier: atelierSelectionne,
      client_data,
    };

    try {
      const response = await dispatch(sendReservation(payload)).unwrap();
      return {
        success: true,
        message: "Réservation envoyée avec succès.",
        payload: response,
      };
    } catch (error) {
      // Redux rejectWithValue renvoie la valeur brute (string ici), pas un Error
      const rawMessage =
        typeof error === "string"
          ? error
          : error?.message || "Erreur lors de l'envoi de la réservation.";

      const message = rawMessage.includes("réservation existe déjà")
        ? "Vous avez déjà une réservation pour ce créneau."
        : rawMessage;

      return { success: false, message };
    }
  }, [
    atelierSelectionne,
    formData,
    dateSelectionnee,
    ateliers,
    dispatch,
  ]);

  useEffect(()=>{
    //conversion
    const annee = dateSelectionnee.getFullYear()
    const mois = dateSelectionnee.getUTCMonth() + 1
    const dateFormate = `${dateSelectionnee.getFullYear()}-${String(dateSelectionnee.getMonth()+1).padStart(2, "0")}-${String(dateSelectionnee.getDate()).padStart(2, "0")}`
    //rafraichissement des données
    
    dispatch(fetchAteliers());
    dispatch(fetchDisponibiliteJour({date:dateFormate, atelier:atelierSelectionne}));
    dispatch(fetchDisponibiliteMois({annee:annee, mois:mois, atelier:atelierSelectionne}))
    
  },[dateSelectionnee, atelierSelectionne])

  return (
    <div className='w-5/6 py-4 px-5 flex flex-col items-left justify-center gap-8'>
        <div>
          <h1 className="text-3xl font-bold">Reservation</h1>
          
        </div>
        <div className='flex flex-col md:flex-row w-full gap-4 items-start md:items-stretch justify-around'>
          <Calendar
          dateSelectionnee={dateSelectionnee} 
          setDateSelectionne = {setDateSelectionnee}
          disponibilite={disponibiliteMois}
          onMonthChange={handleMonthChange}
          />
          <ReservationSelector
           selectedDate={dateSelectionnee} 
           onSelectionChange={handleSelectionChange}
            disponibileJour={disponibiliteJour}
           />
        </div>
        <div>
          <FormReservation
            formData={formData}
            atelierSelectionne={atelierSelectionne}
            creneauSelectionne={creneauSelectionne}
            dateSelectionnee={dateSelectionnee}
            onSubmitReservation={handleReservationSubmit}
          />
          </div>

    </div>
  )
}

export default Reservation
